import { DateTime } from 'luxon';
import flatMap from 'lodash/fp/flatMap';
import { grey, common } from '@material-ui/core/colors';
import deepEqual from 'fast-deep-equal';
import { useIntl } from 'react-intl';
import React from 'react';
import { themeWithoutDir as theme } from '~/modules/App/withRootTheme';
import { ResourceAllocationStatus } from '~/types';
import {
  convertDurationToHours,
  roundNumberWithHighPrecision
} from '~/modules/common/util';
import {
  isoStringToObjectWithCache as isoStringToObject,
  mapIsoStringtoUtcObject,
  mapRepliconDateToUtcObject
} from '~/modules/common/dates/convert';
import { getWorkScheduleFromScheduleRule } from '~/modules/resourcing/common/util/scheduleUtil';
import { TimeOffBookingIcon, ProjectIcon } from '~/modules/common/components';

export const TIME_OFF_MEASUREMENT_UNIT_TYPE = {
  HOURS: 'HOURS',
  DAYS: 'DAYS'
};

export const startDateInISODateFormat = dateString => {
  const d = isoStringToObject(dateString);

  return d ? DateTime.fromObject(d).toISODate() : null;
};

export const endDateISODateFormat = dateString => {
  const date = mapIsoStringtoUtcObject(dateString);
  const shimmedDate = date.plus({ days: 1 });

  return shimmedDate.toISODate();
};

const getAllocationColor = status => {
  switch (status) {
    case ResourceAllocationStatus.Draft:
      return 'pending';
    case ResourceAllocationStatus.Proposed:
      return 'proposed';
    case ResourceAllocationStatus.Committed:
      return 'complete';
    case ResourceAllocationStatus.Rejected:
      return 'resourceRejected';
    default:
      return '';
  }
};

export const mapAllocation = ({ allocation, showIcon, sortOrder }) =>
  allocation.scheduleRules
    .filter(schedule => schedule.do.setHours > 0)
    .flatMap(schedule => {
      const workSchedules = getWorkScheduleFromScheduleRule({
        start: schedule.dateRange.startDate,
        end: schedule.dateRange.endDate,
        excludeWeekdays: schedule.do.excludeWeekdays
      });

      return workSchedules.map(workSchedule => ({
        start: workSchedule.startDate
          ? startDateInISODateFormat(workSchedule.startDate)
          : null,
        end: workSchedule.endDate
          ? endDateISODateFormat(workSchedule.endDate)
          : null,
        title: allocation.project ? allocation.project.displayText : null,
        color:
          theme.palette.resourceRequest[
            getAllocationColor(allocation.allocationStatus)
          ].main,
        allocationStatus: allocation.allocationStatus,
        textColor: theme.palette.getContrastText(
          theme.palette.resourceRequest[
            getAllocationColor(allocation.allocationStatus)
          ].main
        ),
        ...(sortOrder >= 0 && { sortOrder }),
        ...(showIcon && {
          extendedProps: {
            icon: <ProjectIcon fontSize="small" />
          }
        })
      }));
    });

export const mapAllocationEvents = ({
  allocations = [],
  showIcon,
  sortOrder
}) =>
  flatMap(allocation => mapAllocation({ allocation, showIcon, sortOrder }))(
    allocations
  );

export const mapHoliday = ({
  holiday,
  showIcon,
  showGen3Colours,
  sortOrder
}) => ({
  start: mapRepliconDateToUtcObject(holiday.date).toISODate(),
  end: mapRepliconDateToUtcObject(holiday.date).toISODate(),
  title: holiday.name,
  color: showGen3Colours
    ? theme.palette.resourceAllocation.gen3TimeOff
    : grey[600],
  textColor: showGen3Colours ? grey[700] : common.white,
  ...(showGen3Colours && {
    borderColor: grey[300]
  }),
  ...(showIcon && {
    extendedProps: {
      icon: <TimeOffBookingIcon fontSize="small" />
    }
  }),
  ...(sortOrder >= 0 && { sortOrder })
});

export const mapHolidayEvents = ({
  holidays,
  showIcon,
  showGen3Colours,
  sortOrder
}) =>
  flatMap(holiday =>
    mapHoliday({ holiday, showIcon, showGen3Colours, sortOrder })
  )(holidays || []);

export const mapHolidayWithDuration = ({ holiday, formatMessage }) => ({
  start: mapRepliconDateToUtcObject(holiday.date).toISODate(),
  end: mapRepliconDateToUtcObject(holiday.date).toISODate(),
  effectiveDuration: `${roundNumberWithHighPrecision(
    convertDurationToHours(holiday.effectiveDuration)
  )} ${formatMessage({ id: 'calendar.hours' })}`,
  title: holiday.name,
  color: grey[600],
  textColor: common.white
});

export const mapHolidayEventsWithDuration = ({ holidays, formatMessage }) =>
  flatMap(holiday => mapHolidayWithDuration({ holiday, formatMessage }))(
    holidays || []
  );

export const mapTimeoff = ({
  timeoff,
  formatMessage,
  showIcon,
  showGen3Colours,
  sortOrder
}) => {
  const eventClasses = [];
  const totalHours = (timeoff.entries || []).reduce(
    (total, current) => total + current.hours,
    0
  );

  const units = timeoff.units.includes(TIME_OFF_MEASUREMENT_UNIT_TYPE.DAYS)
    ? timeoff.days
    : parseFloat(totalHours.toFixed(2));

  (timeoff.entries || []).forEach(timeoffEntry => {
    if (deepEqual(timeoffEntry.date, timeoff.dateRange.startDate)) {
      timeoffEntry.days === 1 ? null : eventClasses.push('fc-half-days');
    } else if (deepEqual(timeoffEntry.date, timeoff.dateRange.endDate)) {
      timeoffEntry.days === 1 ? null : eventClasses.push('fc-half-day');
    }
  });

  return {
    start: mapRepliconDateToUtcObject(timeoff.dateRange.startDate).toISODate(),
    end: mapRepliconDateToUtcObject(timeoff.dateRange.endDate)
      .plus({ days: 1 })
      .toISODate(),
    title: timeoff.timeOffType.displayText,
    color: showGen3Colours
      ? theme.palette.resourceAllocation.gen3TimeOff
      : grey[700],
    textColor: showGen3Colours ? grey[700] : common.white,
    classNames: eventClasses,
    ...(showGen3Colours && {
      borderColor: grey[300]
    }),
    ...(showIcon && {
      extendedProps: {
        icon: <TimeOffBookingIcon fontSize="small" />
      }
    }),
    description:
      units === 0
        ? timeoff.timeOffType.displayText
        : `${timeoff.timeOffType.displayText}: ${units} ${formatMessage({
            id:
              units > 1
                ? timeoff.units.includes(TIME_OFF_MEASUREMENT_UNIT_TYPE.HOURS)
                  ? 'calendar.hours'
                  : 'calendar.days'
                : timeoff.units.includes(TIME_OFF_MEASUREMENT_UNIT_TYPE.HOURS)
                ? 'calendar.hour'
                : 'calendar.day'
          })}`,
    ...(sortOrder >= 0 && { sortOrder })
  };
};

export const mapTimeoffEvents = ({
  timeoffs = [],
  formatMessage,
  showIcon,
  showGen3Colours,
  sortOrder
}) =>
  flatMap(timeoff =>
    mapTimeoff({ timeoff, formatMessage, showIcon, showGen3Colours, sortOrder })
  )(timeoffs);

export const useEvents = props => {
  const { formatMessage } = useIntl();
  const allocationEvents = mapAllocationEvents(props);
  const timeoffEvents = mapTimeoffEvents({ ...props, formatMessage });
  const holidayEvents = mapHolidayEventsWithDuration({
    holidays: props.holidays,
    formatMessage
  });

  const mergedEvents = allocationEvents.concat(timeoffEvents) || [];

  return {
    events: mergedEvents,
    holidayEvents
  };
};

export default useEvents;
